import { useMemo } from 'react'
import { ClipboardList, Plus, ArrowDown } from 'lucide-react'
import { EmptyState } from '@/components/ui/EmptyState'
import { useSettingsStore } from '@/stores/settingsStore'
import { useTaskStore } from '@/stores/taskStore'

const PRODUCTIVITY_TIPS = [
  '큰 작업은 작은 단계로 나눠보세요.',
  '가장 어려운 일을 먼저 끝내보세요.',
  '2분 안에 끝나는 일은 바로 처리하세요.',
  '완료된 일을 보며 성취감을 느껴보세요.',
  '하루에 3가지 핵심 목표를 정해보세요.',
  '집중 시간을 정해 몰입해보세요.',
  '작은 습관이 큰 변화를 만듭니다.',
  '마감일을 설정하면 실행력이 높아져요.',
]

interface TaskEmptyStateProps {
  className?: string
  onCreateTask?: () => void
}

export function TaskEmptyState({ className, onCreateTask }: TaskEmptyStateProps) {
  const hasCompletedOnboarding = useSettingsStore((s) => s.settings.hasCompletedOnboarding)
  const taskCount = useTaskStore((s) => s.tasks.length)
  const showGuide = hasCompletedOnboarding && taskCount === 0

  const getContextualMessage = () => {
    const hour = new Date().getHours()

    if (hour >= 5 && hour < 12) {
      return {
        title: '좋은 아침입니다! ☀️',
        description: '오늘의 목표를 세우고 하루를 시작해보세요.'
      }
    } else if (hour >= 12 && hour < 18) {
      return {
        title: '오후도 힘내세요! 💪',
        description: '남은 작업들을 차근차근 처리해보세요.'
      }
    } else if (hour >= 18 && hour < 22) {
      return {
        title: '오늘 하루도 수고하셨습니다 🌙',
        description: '남은 작업을 마무리하고 휴식을 취하세요.'
      }
    } else {
      return {
        title: '편안한 밤 되세요 ⭐',
        description: '내일의 계획을 미리 세워보는 건 어떨까요?'
      }
    }
  }

  const { title, description } = getContextualMessage()
  const tip = useMemo(() => PRODUCTIVITY_TIPS[Math.floor(Math.random() * PRODUCTIVITY_TIPS.length)], [])

  return (
    <div className={className}>
      <EmptyState
        icon={<div className="animate-[float_3s_ease-in-out_infinite]"><ClipboardList className="w-full h-full" /></div>}
        title={title}
        description={description}
        action={onCreateTask ? {
          label: '작업 추가',
          onClick: onCreateTask,
          icon: <Plus className="w-4 h-4" />,
        } : undefined}
        size="md"
      />

      {/* Productivity tip */}
      <p className="text-center text-xs text-disabled mt-3 italic">
        💡 {tip}
      </p>

      {/* Interactive guide hint — shown after onboarding when no tasks exist */}
      {showGuide && (
        <div className="flex flex-col items-center mt-4 lg:hidden animate-in fade-in duration-500">
          <p className="text-sm text-caption mb-2">
            하단의 + 버튼을 눌러 첫 작업을 만들어보세요!
          </p>
          <ArrowDown className="w-5 h-5 text-primary-500 animate-bounce" />
        </div>
      )}
    </div>
  )
}
